import { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuthStore } from '../../store/auth-store';
import { usePreferencesStore } from '../../store/preferences-store';
import { api, NETWORK_ERROR_CODE } from '../../services/api';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { VALID_VARIANT_STRINGS } from '@draughts/shared';
import { Eye, EyeOff } from 'lucide-react';
import { trackForgotPasswordRequested, trackAuthModalOpened, trackAuthModalTabSwitched } from '../../utils/analytics';
import { log } from '../../utils/logger';
import { emailAccettataDalBrowser } from '../../utils/email-valida';
import LegalModal from '../legal/LegalModal';
import { CountrySelect } from '../common/CountrySelect';
import VariantSelect from '../profile/VariantSelect';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { detectCountry } from '../../utils/country';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import './AuthModal.css';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
}

type AuthMode = 'login' | 'register' | 'upgrade' | 'forgot';

// Strip zero-width and invisible characters
const stripInvisible = (s: string) =>
  s.replace(/[\u200B\u200C\uFEFF\u00AD\u200E\u200F\u2028\u2029]|\u200D/g, '');

function AuthModal({ isOpen, onClose, initialMode }: AuthModalProps) {
  const { t } = useTranslation();
  // Close on a device back press (before the early return — rules of hooks).
  useOverlayBackButton(isOpen, onClose);
  const [mode, setMode] = useState<AuthMode>(initialMode || 'login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [country, setCountry] = useState<string | null>(() => detectCountry());
  // Seeded from the local preference (a guest who already played keeps their pick;
  // a first-time visitor gets the platform default).
  const [preferredVariant, setPreferredVariant] = useState(
    () => usePreferencesStore.getState().preferredVariant,
  );
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy' | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  /*
   * La correzione dell'indirizzo, dalla stessa schermata che dice «controlla la
   * posta». Un indirizzo sbagliato di una lettera murava l'account: `login`
   * rifiuta finché non è verificato, il recupero password ignora in silenzio gli
   * account non verificati, e il reinvio rimanda proprio all'indirizzo
   * sbagliato — quindi il bottone «reinvia» qui accanto, in questo caso, non
   * serve a niente. Lo username era già speso, e non c'era modo di riprendersi.
   */
  const [correzioneAperta, setCorrezioneAperta] = useState(false);
  const [nuovaEmail, setNuovaEmail] = useState('');
  const [correzioneStato, setCorrezioneStato] = useState<'idle' | 'invio' | 'inviato' | 'errore'>('idle');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  /*
   * Le tre caselle dei consensi, per poterci portare l'utente.
   *
   * Premendo il bottone senza le spunte comparivano tre righe di testo SOPRA
   * il bottone, e nient'altro: nessuno scroll, nessun fuoco, nessun annuncio.
   * Su iPhone e' il caso peggiore di tutti, perche' quelle righe inserite
   * spingono giu' il bottone di ~100px e WebKit — a differenza di Chrome e
   * Firefox — non ha lo scroll anchoring che compensa: il tasto scivola fuori
   * dal bordo inferiore, cioe' scappa da sotto il dito che l'ha appena
   * premuto. Portare la vista sulla casella risolve quello, la tastiera che
   * copre il fondo e il banner dei cookie, tutti in un colpo.
   */
  /*
   * Gli id sono unici PER ISTANZA.
   *
   * `App.tsx` monta due `AuthModal` insieme (una per l'accesso, una per
   * l'upgrade da ospite) e nulla garantisce che non siano aperte entrambe: il
   * foglio del puzzle apre l'upgrade programmaticamente mentre l'altra puo'
   * essere aperta. Con id fissi, `aria-describedby` risolve al PRIMO in ordine
   * di documento e la casella di un modale si descriverebbe col promemoria
   * dell'altro.
   */
  const uid = useId();
  const rifEta = useRef<HTMLInputElement>(null);
  const rifTermini = useRef<HTMLInputElement>(null);
  const rifPrivacy = useRef<HTMLInputElement>(null);

  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const loginAction = useAuthStore((state) => state.login);
  const registerAction = useAuthStore((state) => state.register);
  const upgradeGuest = useAuthStore((state) => state.upgradeGuest);
  const isGuest = useAuthStore((state) => state.isGuest);
  const clearError = useAuthStore((state) => state.clearError);
  const savePreferredVariant = usePreferencesStore((state) => state.setPreferredVariant);

  // Real-time field validation
  /*
   * L'email come la giudica il BROWSER, non `includes('@')`.
   *
   * Il form non ha `noValidate`, quindi il browser esegue la propria
   * validazione PRIMA di emettere `submit`. Il cancello qui sotto era piu'
   * permissivo del validatore nativo: `mario@`, `@gmail.com`, o — il caso vero
   * — un indirizzo con uno spazio in coda lasciato dalla barra QuickType di
   * iOS abilitavano il bottone, e poi il browser abortiva la submission.
   * `handleSubmit` non partiva, quindi `setSubmitAttempted(true)` non veniva
   * mai eseguito e NESSUN promemoria si accendeva.
   *
   * ⚠️ Su Chrome e Firefox il blocco e' accompagnato dal fumetto nativo
   * («Inserisci un indirizzo email») e dallo scroll sul campo. **Safari su iOS
   * non disegna nessun fumetto di validazione**: il tocco spariva nel vuoto, ed
   * era l'unica strada in cui il tasto era letteralmente inerte — solo su
   * Apple.
   *
   * Trimmato perche' l'indirizzo viene poi inviato trimmato (`trimmedEmail`):
   * i due passaggi devono guardare la stessa cosa.
   */
  const emailValido = emailAccettataDalBrowser(email.trim());

  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (mode === 'login') return errors;

    const cleanUsername = stripInvisible(username).trim();
    if (touched.username && cleanUsername.length > 0 && cleanUsername.length < 3) {
      errors.username = t('auth.errors.usernameTooShort');
    }
    if (touched.username && cleanUsername.length > 0 && !/^[a-zA-Z0-9_]*$/.test(cleanUsername)) {
      errors.username = t('auth.validation.usernameChars');
    }

    /*
     * La STESSA verita' del cancello del bottone, non `includes('@')`.
     *
     * Erano due metri diversi, e il divario aveva un costo preciso: per
     * `mario@` o `a@b_c.com` il messaggio taceva (la chiocciola c'e') mentre il
     * bottone restava spento (il browser li rifiuta). L'utente si trovava un
     * tasto pallido e inerte senza una parola che dicesse perche' — su OGNI
     * browser, non solo su Safari. Riparare il cancello senza riparare il
     * messaggio scambiava un vicolo cieco con un altro.
     */
    if (touched.email && email.length > 0 && !emailValido) {
      errors.email = t('auth.validation.invalidEmail');
    }

    if (touched.password && password.length > 0) {
      const missing: string[] = [];
      if (password.length < 8) missing.push(t('auth.validation.min8'));
      if (!/[A-Z]/.test(password)) missing.push(t('auth.validation.uppercase'));
      if (!/[a-z]/.test(password)) missing.push(t('auth.validation.lowercase'));
      if (!/[0-9]/.test(password)) missing.push(t('auth.validation.number'));
      if (missing.length > 0) {
        errors.password = missing.join(' · ');
      }
    }

    if (touched.confirmPassword && confirmPassword.length > 0 && password !== confirmPassword) {
      errors.confirmPassword = t('auth.errors.passwordsNoMatch');
    }

    return errors;
  }, [mode, username, email, emailValido, password, confirmPassword, touched, t]);


  // Only the text fields gate the submit button. The legal checkboxes are
  // intentionally NOT part of this gate: a disabled <button> never receives a
  // click, so keeping the button enabled (once text is valid) lets pressing it
  // surface the "you must tick these boxes" feedback below via handleSubmit.
  const isRegisterTextValid = useMemo(() => {
    if (mode === 'login') return true;
    const cleanUsername = stripInvisible(username).trim();
    return (
      cleanUsername.length >= 3 &&
      /^[a-zA-Z0-9_]+$/.test(cleanUsername) &&
      emailValido &&
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      password === confirmPassword
    );
  }, [mode, username, emailValido, password, confirmPassword]);

  // Per-checkbox error state, revealed only after a submit attempt. Because each
  // includes `!checked`, ticking the box clears its own error automatically.
  const consensiMancanti =
    (ageConfirmed ? 0 : 1) + (termsAccepted ? 0 : 1) + (privacyAccepted ? 0 : 1);
  const ageError = submitAttempted && !ageConfirmed;
  const termsError = submitAttempted && !termsAccepted;
  const privacyError = submitAttempted && !privacyAccepted;

  /*
   * Testo a posto, spunte no: il bottone e' PREMIBILE (il tocco porta alla
   * prima casella) ma non deve sembrare pronto. Non aspetta un primo tentativo:
   * «e' verde anche senza le spunte» descriveva l'aspetto a colpo d'occhio, e
   * un promemoria che arriva solo dopo il tocco non risponde a quello.
   *
   * Il cancello sui campi di testo ci sta dentro apposta: finche' quelli non
   * sono validi il bottone e' `disabled`, un tocco non produce nulla, e
   * annunciare li' che «mancano tre conferme» direbbe la cosa sbagliata su che
   * cosa sta bloccando.
   */
  const inAttesaDiConsensi = mode !== 'login' && isRegisterTextValid && consensiMancanti > 0;
  const testoConsensiMancanti = inAttesaDiConsensi
    ? t('legal.missingConsents', { count: consensiMancanti })
    : '';

  useEffect(() => {
    if (isOpen) {
      /*
       * Il modo si riporta a quello d'INGRESSO a ogni apertura, non solo quando
       * `initialMode` cambia.
       *
       * Prima era `useEffect(..., [initialMode])`: chi apriva l'accesso, dentro
       * premeva «Registrati» (`switchMode` tocca il `mode` interno, non
       * `initialMode`) e poi chiudeva, riaprendo dalla navbar si ritrovava
       * ancora la registrazione — il bottone diceva «Accedi» e mostrava altro.
       * Chiudere non basta a ripulire: la × passa da `handleClose`, ma il gesto
       * «indietro» chiama `onClose` grezzo, e il componente non viene MAI
       * smontato (vedi qui sotto). L'apertura è l'unico momento che li copre
       * entrambi.
       */
      const modoIniziale = initialMode || 'login';
      setMode(modoIniziale);
      /* `mode` qui è ancora quello vecchio: lo stato si aggiorna al render
         dopo. Alla telemetria va detto da dove si è entrati davvero. */
      log.info('AuthModal', 'Modal opened', { tab: modoIniziale });
      trackAuthModalOpened(modoIniziale);
      /*
       * I promemoria non si accendono da soli su un modulo appena aperto.
       *
       * Il componente non viene MAI smontato (`isOpen` falso restituisce
       * `null`, ma resta montato), e le due chiusure hanno semantiche diverse:
       * la × passa da `handleClose` -> `resetForm()`, il gesto «indietro» passa
       * dal `onClose` grezzo e non azzera niente. Chiudendo con l'indietro,
       * `submitAttempted` restava `true` e alla riapertura le tre righe ambra
       * erano gia' accese senza che l'utente avesse premuto nulla — sembra un
       * rimprovero per qualcosa che non ha ancora fatto.
       *
       * Qui si azzera solo il RIMPROVERO, non il modulo: quel che si era
       * scritto resta, ed e' la cosa gentile da fare con chi riapre.
       */
      setSubmitAttempted(false);
      /*
       * E un modulo aperto ADESSO non può mostrare l'esito del giro precedente.
       *
       * `registrationSuccess` (e i suoi fratelli) prendono il posto del modulo:
       * `{mode !== 'forgot' && !registrationSuccess && <form …>}`. Venivano
       * azzerati solo da `resetForm`, cioè dalla × e da Escape — NON dal gesto
       * «indietro», che passa dall'`onClose` grezzo. Su mobile, quindi: ti
       * registri, leggi «controlla la posta», chiudi con l'indietro, premi
       * «Accedi» nella barra e ti ritrovi il pannello di prima, col modulo di
       * accesso soppresso sotto un titolo che dice «Accedi».
       */
      azzeraEsiti();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode]);

  // Resend cooldown timer (must be before early return to respect Rules of Hooks)
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  /*
   * Il fuoco: dentro all'apertura, dentro finché è aperta, indietro alla
   * chiusura. Chiamato PRIMA del `return null` qui sotto perché un hook non può
   * stare dietro a una condizione; `isOpen` è il suo interruttore.
   *
   * `chiudiConEscape` e non `handleClose` diretto: quest'ultimo è dichiarato più
   * in basso (fra le altre azioni del modulo) e qui non esisterebbe ancora. La
   * ref lo raggiunge quando serve davvero, cioè al tasto premuto.
   */
  const chiusuraRef = useRef<() => void>(() => {});
  const chiudiConEscape = useCallback(() => chiusuraRef.current(), []);
  const riferimentoModale = useModaleAccessibile<HTMLDivElement>(isOpen, chiudiConEscape);

  if (!isOpen) return null;

  const markTouched = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  /*
   * Gli ESITI: i pannelli che prendono il posto del modulo quando qualcosa è
   * andato a buon fine (il ✉ «controlla la posta», il «ti ho mandato il link»
   * del recupero password). Separati dal resto di `resetForm` perché vanno via
   * anche a MODULO APERTO — vedi la chiamata nell'effetto d'apertura.
   */
  const azzeraEsiti = () => {
    setRegistrationSuccess(false);
    setRegisteredEmail('');
    setVerificationNeeded(false);
    setResendStatus('idle');
    setCorrezioneAperta(false);
    setNuovaEmail('');
    setCorrezioneStato('idle');
    setForgotSent(false);
    setForgotLoading(false);
  };

  const resetForm = () => {
    setIdentifier('');
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setCountry(detectCountry());
    setPreferredVariant(usePreferencesStore.getState().preferredVariant);
    setTermsAccepted(false);
    setPrivacyAccepted(false);
    setAgeConfirmed(false);
    setSubmitAttempted(false);
    setLocalError(null);
    azzeraEsiti();
    setForgotEmail('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setTouched({});
    clearError();
  };

  const handleResendVerification = async () => {
    if (!identifier || resendCooldown > 0) return;
    log.info('AuthModal', 'Resend verification clicked');
    setResendStatus('sending');
    try {
      await api.resendVerification(identifier);
      setResendStatus('sent');
      setResendCooldown(60);
    } catch {
      setResendStatus('idle');
    }
  };

  /**
   * Manda la verifica a un indirizzo nuovo, e aggiorna quello mostrato.
   *
   * La password è quella appena scelta ed è ancora nello stato del modale: non
   * si chiede di riscriverla, si chiede solo l'indirizzo giusto. Se il modale
   * fosse stato riaperto da capo il campo sarebbe vuoto, e in quel caso il
   * bottone non parte (la guardia qui sotto).
   */
  const inviaCorrezioneEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailPulita = stripInvisible(nuovaEmail).trim().toLowerCase();
    const identificativo = stripInvisible(username).trim() || identifier.trim();
    if (!emailPulita || !password || !identificativo) return;
    log.info('AuthModal', 'Correzione indirizzo non verificato');
    setCorrezioneStato('invio');
    try {
      await api.changeUnverifiedEmail(identificativo, password, emailPulita);
      // La risposta è volutamente identica in ogni caso (niente enumerazione):
      // si mostra il nuovo indirizzo e si torna alla schermata «controlla la
      // posta», che ora punta dove deve.
      setRegisteredEmail(emailPulita);
      setCorrezioneStato('inviato');
      setCorrezioneAperta(false);
      setNuovaEmail('');
    } catch {
      setCorrezioneStato('errore');
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const trimmedEmail = stripInvisible(forgotEmail).trim().toLowerCase();
    if (!trimmedEmail) return;
    log.info('AuthModal', 'Forgot password submitted');
    setForgotLoading(true);
    try {
      await api.forgotPassword(trimmedEmail);
    } catch {
      // Always show success message (don't reveal if account exists)
    } finally {
      setForgotLoading(false);
      setForgotSent(true);
      trackForgotPasswordRequested();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();

    // Trim inputs before sending
    const trimmedUsername = stripInvisible(username).trim();
    const trimmedEmail = email.trim();
    const trimmedIdentifier = identifier.trim();

    if (mode === 'register' || mode === 'upgrade') {
      log.info('AuthModal', 'Register submitted', { mode });
      setSubmitAttempted(true);
      if (password !== confirmPassword) {
        setLocalError(t('auth.errors.passwordsNoMatch'));
        return;
      }
      if (password.length < 8) {
        setLocalError(t('auth.errors.passwordTooShort'));
        return;
      }
      if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
        setLocalError(t('auth.errors.passwordRequirements'));
        return;
      }
      if (trimmedUsername.length < 3) {
        setLocalError(t('auth.errors.usernameTooShort'));
        return;
      }
      // Missing consents are a pending step, not a failure: `submitAttempted`
      // already reveals the amber per-checkbox reminders, so deliberately no
      // red banner here (the banner stays for real errors: password, network, server).
      if (!ageConfirmed || !termsAccepted || !privacyAccepted) {
        /*
         * E ci si porta l'utente. La prima non spuntata, al centro dello
         * schermo: `block: 'center'` la mette sopra la tastiera e sopra il
         * banner dei cookie, e uno scroll esplicito rende irrilevante il fatto
         * che WebKit non ancori lo scroll quando i promemoria si inseriscono.
         *
         * `preventScroll` sul fuoco perche' lo scorrimento l'ha gia' fatto la
         * riga sopra, e due scroll in fila danno uno scatto.
         */
        const primaMancante = !ageConfirmed ? rifEta.current
          : !termsAccepted ? rifTermini.current
          : rifPrivacy.current;
        /*
         * ⚠️ Prima si fa cadere la TASTIERA.
         *
         * Su iOS il layout viewport NON si accorcia quando la tastiera sale
         * (si accorcia solo il visual viewport), quindi `block: 'center'`
         * centra rispetto a un riquadro che la tastiera copre per meta': la
         * casella poteva finire proprio dietro. Togliendo il fuoco al campo la
         * tastiera scende e il centro torna a essere il centro di quel che si
         * vede.
         */
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        /* Il moto ridotto e' una preferenza che il progetto rispetta ovunque
           (`ProfileBackdrop`, `useCountUp`, e in questo stesso foglio di stile). */
        const motoRidotto = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        primaMancante?.scrollIntoView({ block: 'center', behavior: motoRidotto ? 'auto' : 'smooth' });
        primaMancante?.focus({ preventScroll: true });
        return;
      }
      try {
        if (mode === 'upgrade') {
          await upgradeGuest({ username: trimmedUsername, email: trimmedEmail, password, termsAccepted: true, privacyAccepted: true, ageConfirmed: true });
          // Upgrade authenticates immediately, so the store sync (PATCH) carries
          // the pick to the server — the register payload can't (no session yet).
          savePreferredVariant(preferredVariant);
          resetForm();
          onClose();
        } else {
          await registerAction({ username: trimmedUsername, email: trimmedEmail, password, country: country ?? undefined, preferredVariant, termsAccepted: true, privacyAccepted: true, ageConfirmed: true });
          savePreferredVariant(preferredVariant);
          setRegisteredEmail(trimmedEmail);
          setRegistrationSuccess(true);
        }
      } catch (err) {
        // For upgrade, if verification is required (server returns requiresVerification)
        if (err instanceof Error && err.message.includes('verify')) {
          setRegisteredEmail(trimmedEmail);
          setRegistrationSuccess(true);
        }
        // Otherwise error handled by store
      }
    } else {
      log.info('AuthModal', 'Login submitted');
      try {
        await loginAction({ identifier: trimmedIdentifier, password });
        resetForm();
        onClose();
      } catch (err) {
        if (err instanceof Error && err.message.includes('verify')) {
          setVerificationNeeded(true);
        }
      }
    }
  };

  const switchMode = () => {
    /*
     * Per un OSPITE «Registrati» vuol dire conversione, non conto nuovo.
     *
     * «Accedi» non si dirotta mai — e' l'unica via di rientro per un utente
     * registrato che sta giocando da ospite, e dirottarlo lo murava fuori (vedi
     * `apriAccesso` in App.tsx). Ma da li' dentro un click su «Registrati»
     * finiva su `register`, cioe' su un conto NUOVO: le partite dell'ospite, il
     * rating dei puzzle e lo Streak restavano appesi a un'identita' che nessuno
     * avrebbe piu' riaperto. E non da' nessun errore: il conto vecchio sparisce
     * e basta.
     *
     * La porta d'ingresso resta aperta in tutti e due i versi: da qui si torna
     * sempre ad «Accedi» (vedi il blocco in fondo, che per questo motivo compare
     * anche in conversione).
     */
    const newMode = mode === 'login' ? (isGuest ? 'upgrade' : 'register') : 'login';
    log.info('AuthModal', 'Tab switched', { tab: newMode });
    setMode(newMode);
    trackAuthModalTabSwitched(newMode);
    setLocalError(null);
    setTouched({});
    setSubmitAttempted(false);
    clearError();
  };

  const handleClose = () => {
    log.info('AuthModal', 'Modal closed');
    resetForm();
    onClose();
  };
  // La chiusura che Escape deve chiamare, aggiornata a ogni render.
  chiusuraRef.current = handleClose;

  const displayError = localError || (error === NETWORK_ERROR_CODE ? t('auth.errors.network') : error);

  // Password requirements indicator
  const pwReqs = password.length > 0 ? {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  } : null;

  return (
    /*
     * Chiude SOLO se il tocco e' finito davvero sullo sfondo.
     *
     * `onClick` sull'overlay riceve anche i click che risalgono da qualunque
     * discendente, e `handleClose` chiama `resetForm()`: un gestore annidato
     * che non fermi la propagazione azzerava tutto il modulo. E' successo col
     * modale legale, che vive qui dentro (vedi `LegalModal`), ed e' li'
     * riparato — ma `e.target === e.currentTarget` e' la difesa che vale per
     * QUALUNQUE annidamento futuro, invece che per quello che conosciamo.
     */
    <div
      className="auth-modal-overlay auth-modal-overlay--fullscreen"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      {/*
        * `role="dialog"` + `aria-modal` + il fuoco governato (`useModaleAccessibile`).
        *
        * Questa finestra non aveva niente di tutto ciò: chi la apriva da tastiera
        * continuava a tabulare nella PAGINA SOTTO, che è ancora lì sotto il velo,
        * e uno screen reader non annunciava nemmeno che si fosse aperto qualcosa —
        * il modale d'accesso, cioè la porta d'ingresso all'app. Escape non
        * chiudeva, e alla chiusura il fuoco non tornava a chi l'aveva.
        *
        * `aria-labelledby` punta al titolo che c'è già: il nome del dialogo deve
        * essere quello che si legge dentro, non una stringa parallela che può
        * divergere.
        */}
      <div
        ref={riferimentoModale}
        className="auth-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="auth-modal-close" onClick={handleClose} type="button" aria-label={t('common.close')}>
          &times;
        </button>
        <h2 id="auth-modal-titolo">{mode === 'forgot' ? t('auth.forgotPassword') : mode === 'login' ? t('auth.signIn') : mode === 'upgrade' ? t('auth.guestUpgrade') : t('auth.createAccount')}</h2>

        {displayError && (
          /*
           * `role="alert"`: è l'errore della registrazione o dell'accesso, e
           * compare dopo l'invio. Senza ruolo uno screen reader non lo annuncia —
           * chi non vede lo schermo premeva «Crea account» e non riceveva NIENTE,
           * né una conferma né un motivo. È il caso più grave dei sei, perché è la
           * porta d'ingresso all'app.
           *
           * `alert` e non `status`: interrompe, perché è la risposta all'azione
           * appena compiuta e senza di essa non si va avanti.
           */
          <div className="auth-error" role="alert">
            {displayError}
            {verificationNeeded && (
              <div className="auth-resend-row">
                {resendStatus === 'sent' ? (
                  <span className="auth-resend-sent">{t('auth.verifyEmail.resendSent')}</span>
                ) : (
                  <button
                    type="button"
                    className="auth-resend-link"
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending' || resendCooldown > 0}
                  >
                    {resendStatus === 'sending' ? '...' : resendCooldown > 0 ? `${t('auth.verifyEmail.resendLink')} (${resendCooldown}s)` : t('auth.verifyEmail.resendLink')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {registrationSuccess && (
          <div className="auth-verification-message">
            <div className="auth-verification-icon">✉</div>
            <h3>{t('auth.verifyEmail.title')}</h3>
            <p>{t('auth.verifyEmail.checkInbox', { email: registeredEmail })}</p>
            <p className="auth-verification-hint">{t('auth.verifyEmail.spamHint')}</p>

            {correzioneStato === 'inviato' && (
              <p className="auth-verification-hint" role="status">{t('auth.verifyEmail.changeSent')}</p>
            )}

            {/* «Indirizzo sbagliato?» — la via d'uscita. Senza, un refuso
                nell'email mura l'account per sempre: da non verificati non si
                accede, il recupero password tace, e il reinvio va di nuovo
                all'indirizzo sbagliato. Il campo compare solo se si preme,
                perché il caso normale è che l'indirizzo sia giusto. */}
            {password && !correzioneAperta && (
              <div className="auth-resend-row">
                <button
                  type="button"
                  className="auth-resend-link"
                  onClick={() => { setCorrezioneAperta(true); setCorrezioneStato('idle'); }}
                >
                  {t('auth.verifyEmail.wrongAddress')} {t('auth.verifyEmail.changeAddress')}
                </button>
              </div>
            )}

            {correzioneAperta && (
              <form className="auth-email-fix" onSubmit={inviaCorrezioneEmail}>
                <div className="auth-field">
                  <label htmlFor="auth-nuova-email">{t('auth.verifyEmail.newAddressLabel')}</label>
                  <input
                    id="auth-nuova-email"
                    type="email"
                    value={nuovaEmail}
                    onChange={(e) => setNuovaEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
                {correzioneStato === 'errore' && (
                  <p className="auth-error" role="alert">{t('auth.verifyEmail.changeFailed')}</p>
                )}
                <button type="submit" className="auth-submit" disabled={correzioneStato === 'invio' || !nuovaEmail.trim()}>
                  {t('auth.verifyEmail.changeSubmit')}
                </button>
                <div className="auth-resend-row">
                  <button type="button" className="auth-resend-link" onClick={() => setCorrezioneAperta(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            )}

            <button
              type="button"
              className="auth-submit"
              onClick={handleClose}
            >
              {t('common.close')}
            </button>
          </div>
        )}

        {mode === 'forgot' && !registrationSuccess && (
          forgotSent ? (
            <div className="auth-verification-message">
              <div className="auth-verification-icon">&#x2709;</div>
              <h3>{t('auth.resetEmailSent')}</h3>
              <p>{t('auth.forgotPasswordDesc')}</p>
              <p className="auth-verification-hint">{t('auth.checkSpam')}</p>
              <button
                type="button"
                className="auth-submit"
                onClick={() => { resetForm(); setMode('login'); }}
              >
                {t('auth.backToLogin')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <p className="auth-forgot-desc">{t('auth.forgotPasswordDesc')}</p>
              <div className="auth-field">
                <label htmlFor="forgotEmail">{t('auth.fields.email')}</label>
                <input
                  id="forgotEmail"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder={t('auth.placeholders.yourEmail')}
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="auth-submit"
                /*
                 * Lo stesso metro dell'iscrizione. Qui il cancello era
                 * `!forgotEmail.trim()`, cioe' «qualcosa c'e' scritto»: un
                 * indirizzo malformato abilitava il bottone, il browser
                 * abortiva la sottomissione, e su Safari iOS — dove il fumetto
                 * di validazione non viene disegnato — il tocco spariva nel
                 * vuoto. Stesso vicolo cieco, altro modulo.
                 */
                disabled={forgotLoading || !emailAccettataDalBrowser(forgotEmail.trim())}
              >
                {forgotLoading ? <span className="auth-spinner" /> : t('auth.sendResetLink')}
              </button>
              <p className="auth-switch">
                <button type="button" onClick={() => { resetForm(); setMode('login'); }}>
                  {t('auth.backToLogin')}
                </button>
              </p>
            </form>
          )
        )}

        {mode !== 'forgot' && !registrationSuccess && <form onSubmit={handleSubmit}>
          {(mode === 'register' || mode === 'upgrade') ? (
            <>
              <div className="auth-field">
                <label htmlFor="username">{t('auth.fields.username')}</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onBlur={() => markTouched('username')}
                  placeholder={t('auth.placeholders.chooseUsername')}
                  required
                  minLength={3}
                  maxLength={30}
                  autoComplete="username"
                  className={fieldErrors.username ? 'auth-input-error' : ''}
                />
                {fieldErrors.username && <span className="auth-field-error">{fieldErrors.username}</span>}
              </div>
              <div className="auth-field">
                <label htmlFor="email">{t('auth.fields.email')}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => markTouched('email')}
                  placeholder={t('auth.placeholders.yourEmail')}
                  required
                  autoComplete="email"
                  className={fieldErrors.email ? 'auth-input-error' : ''}
                />
                {fieldErrors.email && <span className="auth-field-error">{fieldErrors.email}</span>}
              </div>
              <div className="auth-field">
                <label htmlFor="password">{t('auth.fields.password')}</label>
                <div className="auth-password-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => markTouched('password')}
                    placeholder={t('auth.placeholders.atLeast8Chars')}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className={fieldErrors.password ? 'auth-input-error' : ''}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {touched.password && pwReqs && (
                  <div className="auth-pw-requirements">
                    <span className={pwReqs.length ? 'met' : ''}>8+</span>
                    <span className={pwReqs.upper ? 'met' : ''}>A-Z</span>
                    <span className={pwReqs.lower ? 'met' : ''}>a-z</span>
                    <span className={pwReqs.number ? 'met' : ''}>0-9</span>
                  </div>
                )}
              </div>
              <div className="auth-field">
                <label htmlFor="confirmPassword">{t('auth.fields.confirmPassword')}</label>
                <div className="auth-password-wrapper">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => markTouched('confirmPassword')}
                    placeholder={t('auth.placeholders.confirmYourPassword')}
                    required
                    autoComplete="new-password"
                    className={fieldErrors.confirmPassword ? 'auth-input-error' : ''}
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.confirmPassword && <span className="auth-field-error">{fieldErrors.confirmPassword}</span>}
              </div>
              {/* Credentials first, then the optional profile fields — nothing
                  stands between the account essentials and the sign-up button. */}
              <div className="auth-field">
                <label htmlFor="country">{t('profile.country', 'Country')}</label>
                <CountrySelect
                  id="country"
                  value={country}
                  onChange={setCountry}
                  aria-label={t('profile.country', 'Country')}
                />
              </div>
              <div className="auth-field">
                {/* No htmlFor: VariantSelect renders a listbox button, not an input. */}
                <label>{t('profile.preferredVariant', 'Preferred variant')}</label>
                {/* Niente variante appuntata in cima, a differenza degli altri dieci
                    menu dell'app: QUESTO e' il comando che imposta la preferenza, e
                    appuntarci il valore corrente sarebbe una tautologia — il
                    separatore staccherebbe la voce scelta dalle altre fra cui si sta
                    scegliendo. Lo stesso vale per il gemello nelle impostazioni del
                    profilo. Non e' una dimenticanza: non va «riparato». */}
                <VariantSelect
                  value={preferredVariant}
                  onChange={setPreferredVariant}
                  variants={VALID_VARIANT_STRINGS}
                  label={(v) => variantShortLabel(t, v)}
                  dotColor={() => 'transparent'}
                  renderLeading={(v) => variantPill(v).flag}
                  ariaLabel={t('profile.preferredVariant', 'Preferred variant')}
                />
                <span className="auth-field-hint">{t('auth.preferredVariantHint')}</span>
              </div>
              {/* Unticked boxes get an amber reminder (never red) and no top
                  error banner: a step still to complete, not an error. */}
              <div className={`auth-terms-checkbox${ageError ? ' auth-terms-checkbox--pending' : ''}`}>
                <label className="auth-terms-label">
                  <input
                    ref={rifEta}
                    type="checkbox"
                    checked={ageConfirmed}
                    aria-invalid={ageError}
                    aria-describedby={ageError ? `${uid}-eta` : undefined}
                    onChange={(e) => { setAgeConfirmed(e.target.checked); setLocalError(null); }}
                  />
                  <span>{t('legal.ageConfirm')}</span>
                </label>
                {ageError && <span id={`${uid}-eta`} className="auth-legal-hint">{t('legal.ageRequired')}</span>}
              </div>
              <div className={`auth-terms-checkbox${termsError ? ' auth-terms-checkbox--pending' : ''}`}>
                <label className="auth-terms-label">
                  <input
                    ref={rifTermini}
                    type="checkbox"
                    checked={termsAccepted}
                    aria-invalid={termsError}
                    aria-describedby={termsError ? `${uid}-termini` : undefined}
                    onChange={(e) => { setTermsAccepted(e.target.checked); setLocalError(null); }}
                  />
                  <span>
                    <Trans i18nKey="legal.acceptTermsOnly" components={{
                      terms: <button type="button" className="auth-terms-link"
                        onClick={(e) => { e.preventDefault(); setLegalModalType('terms'); }} />,
                    }} />
                  </span>
                </label>
                {termsError && <span id={`${uid}-termini`} className="auth-legal-hint">{t('legal.acceptTermsRequired')}</span>}
              </div>
              <div className={`auth-terms-checkbox${privacyError ? ' auth-terms-checkbox--pending' : ''}`}>
                <label className="auth-terms-label">
                  <input
                    ref={rifPrivacy}
                    type="checkbox"
                    checked={privacyAccepted}
                    aria-invalid={privacyError}
                    aria-describedby={privacyError ? `${uid}-privacy` : undefined}
                    onChange={(e) => { setPrivacyAccepted(e.target.checked); setLocalError(null); }}
                  />
                  <span>
                    <Trans i18nKey="legal.acceptPrivacyOnly" components={{
                      privacy: <button type="button" className="auth-terms-link"
                        onClick={(e) => { e.preventDefault(); setLegalModalType('privacy'); }} />,
                    }} />
                  </span>
                </label>
                {privacyError && <span id={`${uid}-privacy`} className="auth-legal-hint">{t('legal.acceptPrivacyRequired')}</span>}
              </div>
            </>
          ) : (
            <>
              <div className="auth-field">
                <label htmlFor="identifier">{t('auth.fields.emailOrUsername')}</label>
                <input
                  id="identifier"
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('auth.placeholders.enterEmailOrUsername')}
                  required
                  autoComplete="username"
                />
              </div>
              <div className="auth-field">
                <label htmlFor="loginPassword">{t('auth.fields.password')}</label>
                <div className="auth-password-wrapper">
                  <input
                    id="loginPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('auth.placeholders.yourPassword')}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="auth-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="auth-forgot-link">
                <button type="button" onClick={() => { log.info('AuthModal', 'Password reset link clicked'); resetForm(); setMode('forgot'); }}>
                  {t('auth.forgotPassword')}
                </button>
              </div>
            </>
          )}

          <button
            type="submit"
            className={`auth-submit${inAttesaDiConsensi ? ' auth-submit--in-attesa' : ''}`}
            disabled={isLoading || (mode !== 'login' && !isRegisterTextValid)}
          >
            {isLoading ? (
              <span className="auth-spinner" />
            ) : (
              <span className="auth-submit__etichetta">
                {mode === 'login'
                  ? t('auth.signIn')
                  : mode === 'upgrade'
                    ? t('auth.guestUpgrade')
                    : t('auth.createAccount')}
              </span>
            )}
            {/*
              * Il riscontro DOVE il dito ha premuto: dentro al bottone.
              *
              * I promemoria per casella stanno SOPRA il bottone, e sopra il
              * bottone su un telefono vuol dire «fuori dallo schermo»: le righe
              * che compaiono spingono giu' il tasto di un centinaio di pixel e
              * WebKit — a differenza di Chrome e Firefox — non ha lo scroll
              * anchoring che compensa. Il riepilogo sta qui, sull'unica cosa
              * che l'utente sta guardando: il tasto che vorrebbe premere.
              *
              * `aria-hidden` perche' questa riga NON deve entrare nel nome
              * accessibile: il bottone si chiama «Crea Account» e basta, sempre.
              * A dirlo a chi non vede ci pensa la regione annunciata qui sotto.
              */}
            {inAttesaDiConsensi && (
              <span className="auth-submit__mancanti" aria-hidden="true">
                {testoConsensiMancanti}
              </span>
            )}
          </button>
          {/*
            * Lo stesso riepilogo, per chi non lo vede.
            *
            * Non e' un doppione della riga dentro al bottone: quella e'
            * `aria-hidden` apposta, per non entrare nel nome accessibile del
            * tasto. Questa e' l'UNICA regione annunciata di tutto il modulo —
            * tre `role="alert"` simultanei si interrompevano a vicenda (NVDA
            * legge solo l'ultimo, cioe' il meno pertinente) e competevano con
            * l'annuncio del fuoco. Un messaggio, un annuncio.
            *
            * ⚠️ Si monta con il MODO, non con `inAttesaDiConsensi`: una regione
            * viva annuncia i cambiamenti di un elemento gia' presente, quindi
            * se comparisse insieme al proprio testo non direbbe niente. E' il
            * testo dentro a svuotarsi, non il `<p>` a sparire.
            *
            * Stessa espressione della riga visibile: annunciare un conteggio
            * che a video non c'e' (campi di testo ancora da sistemare, bottone
            * spento) direbbe la cosa sbagliata su cosa sta bloccando.
            */}
          {mode !== 'login' && (
            <p className="auth-annuncio-consensi" role="status" aria-live="polite">
              {testoConsensiMancanti}
            </p>
          )}
        </form>}

        {/*
          * I due versi non sono simmetrici, e non devono esserlo.
          *
          * Chi arriva all'ACCESSO senza un conto è a un vicolo cieco: l'unica
          * porta della registrazione, in tutta l'app, è questa. Perciò qui è un
          * bottone vero, staccato dal modulo — non una scritta sottolineata da
          * 0.9rem in fondo, che è quello che era e che nessuno vedeva. Resta
          * comunque contornato: «Accedi» sopra è pieno, e il primario è quello.
          *
          * Dal verso opposto (registrazione → accesso) la scritta basta: chi un
          * conto ce l'ha lo sa già, e cerca il rientro, non lo scopre.
          */}
        {/*
          * La riga «hai gia' un conto? Accedi» compare anche in CONVERSIONE.
          *
          * Prima no, perche' li' ci si arrivava solo di proposito dal banner
          * dell'ospite. Adesso ci arriva anche chi ha premuto «Registrati» dentro
          * al modale d'accesso, e senza questa riga si troverebbe in una stanza
          * senza porta — la stessa via murata che il dirottamento di «Accedi»
          * aveva gia' prodotto una volta.
          */}
        {!registrationSuccess && mode !== 'forgot' && (
          mode === 'login' ? (
            <div className="auth-switch auth-switch-block">
              <span className="auth-switch-sep">{t('common.or')}</span>
              <p className="auth-switch-lead">{t('auth.noAccount')}</p>
              <button type="button" className="auth-switch-cta" onClick={switchMode}>
                {t('auth.signUp')}
              </button>
            </div>
          ) : (
            <p className="auth-switch">
              {t('auth.hasAccount')}{' '}
              <button type="button" onClick={switchMode}>
                {t('auth.signIn')}
              </button>
            </p>
          )
        )}

      </div>
      <LegalModal
        type={legalModalType || 'terms'}
        isOpen={legalModalType !== null}
        onClose={() => setLegalModalType(null)}
      />
    </div>
  );
}

export default AuthModal;
